<template>
  <oie-my-pages-template :title="t('invoiceHistoryTitle')" :text="t('invoiceHistorySubTitle')" class="my-invoice-history">
    <div class="my-invoice-history__filter-grid">
      <div>
        <!-- Sök o lägg till artiklar -->
        <oie-base-input
          id="my-invoice-history-search-invoice"
          v-model="searchPhrase"
          :label-text="t('invoiceHistorySearchLabel')"
          epi-edit="'SearchPhraseDescription'"
          :placeholder="t('invoiceHistorySearchPlaceholder')"
          class="my-invoice-history__search-input"
          @input="search"></oie-base-input>
      </div>

      <div class="input my-invoice-history__date-inputs">
        <label v-epi-edit="'FromDateHeader'" for="invoice-history-search-date-from" class="input__label">{{ t('invoiceHistoryFromDateLabel') }}</label>

        <div class="input__container">
          <datepicker
            ref="fromDate"
            v-model="fromDate"
            :placeholder="t('invoiceHistoryFromDateLabel')"
            :monday-first="true"
            wrapper-class="datepicker"
            calendar-class="datepicker-calendar"
            input-class="datepicker-input"
            format="yyyy-MM-dd"
            :language="languages[0]"
            :full-month-name="true"
            @input="search"></datepicker>
        </div>
      </div>
      <div>
        <label v-epi-edit="'ToDateHeader'" for="invoice-history-search-date-from" class="input__label">{{ t('invoiceHistoryToDateLabel') }}</label>

        <div class="input__container">
          <datepicker
            v-model="toDate"
            :placeholder="t('invoiceHistoryToDateLabel')"
            :monday-first="true"
            wrapper-class="datepicker"
            calendar-class="datepicker-calendar"
            input-class="datepicker-input"
            format="yyyy-MM-dd"
            :language="languages[0]"
            :full-month-name="true"
            @input="search"></datepicker>
        </div>
      </div>
    </div>
    <div class="my-invoice-history__filter-row">
      <div class="my-invoice-history__document-type">
        <span class="input__label my-invoice-history__document-type-label">{{ t('headerInvoiceType') }}</span>
        <!-- The site's own dropdown, as on Mina maskiner, rather than a native select (OC-1627). -->
        <oie-base-dropdown
          v-model="documentTypeFilter"
          :aria-label="t('headerInvoiceType')"
          :initial-text="t('showAllInvoicesFilterLabel')"
          :options="documentTypeOptions"
          class="my-invoice-history__document-type-dropdown"></oie-base-dropdown>
      </div>

      <fieldset class="my-invoice-history__filter-actions">
        <oie-base-radio-checkbox
          v-model="showInvoicesFilter"
          value="All"
          class="sign-in-box__checkbox"
          :label="t('showAllInvoicesFilterLabel')"
          @change="search">
        </oie-base-radio-checkbox>

        <oie-base-radio-checkbox
          v-model="showInvoicesFilter"
          value="Open"
          class="sign-in-box__checkbox"
          :label="t('showOpenInvoicesFilterLabel')"
          @change="search">
        </oie-base-radio-checkbox>

        <oie-base-radio-checkbox
          v-model="showInvoicesFilter"
          value="Due"
          class="sign-in-box__checkbox"
          :label="t('showDueInvoicesFilterLabel')"
          @change="search">
        </oie-base-radio-checkbox>
      </fieldset>
    </div>

    <section>
      <div class="my-invoice-history__column-header">
        <h3 v-epi-edit="'OrderListHeader'">
          {{ t('invoiceListHeader') }}
        <!-- Fakturalista-->
        </h3>
      </div>

      <div class="my-invoice-history__table">
        <oie-invoice-history-table v-element-loader="{ endpoint: 'invoice/search' }" :invoices="filteredInvoices" :invoices-filter="showInvoicesFilterParam"></oie-invoice-history-table>
      </div>
    </section>
  </oie-my-pages-template>
</template>

<script>
import Datepicker from 'vuejs-datepicker/dist/vuejs-datepicker.esm.js'
import { sv, de, sr } from 'vuejs-datepicker/dist/locale'
import httpService from '@/core/http-service'
import _ from 'lodash'

export default {
  components: {
    Datepicker
  },

  data() {
    return {
      invoices: [],
      languages: [sv, de, sr],
      fromDate: null,
      toDate: null,
      searchPhrase: '',
      showInvoicesFilter: 'All',
      documentTypeFilter: '',
      moreToGet: false
    }
  },
  computed: {
    // Faktura, Kredit and Kvitto are always offered, as the ticket asks, so the filter is there
    // even when the current result holds one type or none. Any other type that comes back -
    // Betalning, say - is added after them (OC-1627).
    documentTypes() {
      const fixedTypes = [this.t('invoiceDocumentType'), this.t('creditDocumentType'), this.t('receiptDocumentType')]
      const returnedTypes = this.invoices.map(invoice => invoice.documentType).filter(Boolean)

      return _.uniq(fixedTypes.concat(_.difference(returnedTypes, fixedTypes).sort()))
    },
    // In the shape the site dropdown takes. "Visa alla" is the empty value, which the dropdown shows
    // as its initial text.
    documentTypeOptions() {
      return [{ value: '', text: this.t('showAllInvoicesFilterLabel') }]
        .concat(this.documentTypes.map(type => ({ value: type, text: type })))
    },
    // Filtered here rather than in the request: the whole result set is already loaded, and the
    // status filter is the only one the invoice API understands (OC-1627).
    filteredInvoices() {
      if (!this.documentTypeFilter) {
        return this.invoices
      }

      return this.invoices.filter(invoice => invoice.documentType === this.documentTypeFilter)
    },
    showInvoicesFilterParam() {
      if (this.showInvoicesFilter === 'Open') {
        return true
      } else if (this.showInvoicesFilter === 'Due') {
        return false
      }
      return null
    }
  },

  mounted() {
    this.toDate = new Date()
    this.fromDate = new Date()
    this.fromDate.setFullYear(this.fromDate.getFullYear() - 1)
    this.search()
  },
  methods: {
    search: _.debounce(function() {
      httpService.post('invoice/search', { fromDate: this.fromDate, toDate: this.toDate, searchPhrase: this.searchPhrase, showInvoicesFilter: this.showInvoicesFilterParam }).then((response) => {
        this.invoices = response.data.invoices

        for (let i = 0; i < this.invoices.length; i++) {
          this.invoices[i].documentType = this.invoices[i].documentType.replace('Credit Memo', this.t('creditDocumentType'))
          this.invoices[i].documentType = this.invoices[i].documentType.replace('Credit_x0020_Memo', this.t('creditDocumentType'))
          this.invoices[i].documentType = this.invoices[i].documentType.replace('Receipt', this.t('receiptDocumentType'))
          this.invoices[i].documentType = this.invoices[i].documentType.replace('Invoice', this.t('invoiceDocumentType'))
          this.invoices[i].documentType = this.invoices[i].documentType.replace('Payment', this.t('paymentDocumentType'))

          this.invoices[i].displayStatus = this.invoices[i].status
          this.invoices[i].displayStatus = this.invoices[i].displayStatus.replace('NotPaid', this.t('notPaidStatus'))
          this.invoices[i].displayStatus = this.invoices[i].displayStatus.replace('Paid', this.t('paidStatus'))
          this.invoices[i].displayStatus = this.invoices[i].displayStatus.replace('Cancelled', this.t('cancelledStatus'))
          this.invoices[i].displayStatus = this.invoices[i].displayStatus.replace('Overdue', this.t('overdueStatus'))
          this.invoices[i].displayStatus = this.invoices[i].displayStatus.replace('Annulled', this.t('annulledStatus'))
          this.invoices[i].displayStatus = this.invoices[i].displayStatus.replace('Open', this.t('openStatus'))
          this.invoices[i].displayStatus = this.invoices[i].displayStatus.replace('Payment', this.t('paymentStatus'))
          // 'Used' is a substring of 'Unused', so the longer one has to be replaced first or an
          // unused credit comes out with the wrong half of the word swapped (OC-1574).
          this.invoices[i].displayStatus = this.invoices[i].displayStatus.replace('Unused', this.t('unusedStatus'))
          this.invoices[i].displayStatus = this.invoices[i].displayStatus.replace('Used', this.t('usedStatus'))

          this.invoices[i].orderNumber = this.invoices[i].orderNumber.replace(/;$/, '')
          this.invoices[i].orderNumber = this.invoices[i].orderNumber.replaceAll(';', ', ')

          // The empty due date used to be blanked here by matching the string '0001-01-01', which
          // only held under some server cultures - '01/01/0001' went straight to the table. The
          // server now decides which documents have a due date at all (OC-1574).
        }

        // An extra type (not Faktura, Kredit or Kvitto) that is no longer in the result would not be
        // in the list any more, so the filter drops back to showing everything.
        if (this.documentTypeFilter && !this.documentTypes.includes(this.documentTypeFilter)) {
          this.documentTypeFilter = ''
        }

        this.moreToGet = response.data.moreToGet
      })
    }, 300)
  }
}
</script>

<style lang="scss" scoped>
.my-invoice-history {
  min-height: 35rem;
}
.my-invoice-history__filter-grid {
  display: grid;
  gap: measure(quarter);
  margin-bottom: measure(half);
  grid-template-columns: repeat(1, minmax(0, 1fr));

  @include media(medium) {
    grid-template-columns: minmax(0,40%) minmax(0,30%) minmax(0,30%);
  }
}

.my-invoice-history__search-input {
  grid-column: span 3;

  @include media(medium) {
    grid-column: span 1;

  }

}

.my-invoice-history__date-inputs {
  @include media(to-small) {
    grid-column: span 1;
  }
}

.my-invoice-history__column-header {
  @include h3-semi-bold;
  margin-top: 0;
  margin-bottom: measure(eighth) !important;
}

// The document type sits on the same line as the status radios, just before "Visa alla", so the
// two filters read as one row instead of the type floating on its own below (OC-1627).
.my-invoice-history__filter-row {
  display: flex;
  flex-direction: column;
  gap: measure(half);
  margin-bottom: measure(half);

  @include media(small) {
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  }
}

.my-invoice-history__filter-actions {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: measure(half);
}

.my-invoice-history__document-type {
  display: flex;
  align-items: center;
  gap: measure(quarter);
}

.my-invoice-history__document-type-label {
  margin-bottom: 0;
  white-space: nowrap;
}

.my-invoice-history__document-type-dropdown {
  flex: 1;
  min-width: 14rem;
}

.my-invoice-history__filter-actions {
  @include media(small) {
    flex-direction: row;
    align-items: center;
  }
}

.my-invoice-history__filter-actions > * {
   @include media(large) {
    margin-right: measure(half);
  }
}

.my-invoice-history__date-search {
  width: 100%;

  @include media(small) {
    flex-basis: 9.375rem;
    width: auto;
    margin-left: measure(full);
  }

  @include media(medium) {
    align-self: flex-end;
  }
}

.my-invoice-history__table {
  overflow-x: auto;
}

  .my-invoice-history__heading-and-filter-actions {
    display: grid;
    grid-template-columns: minmax(0,40%) minmax(0,18%) minmax(0,22%) minmax(0,20%);
  }

  .show-invoices-filter-radio {
    justify-self: end;
  }

  .my-invoice-history__heading-and-filter-actions .radio__element:checked + .radio__label:before {
    background-color: color(brand-blue-3)  !important;
    border-color: color(green-2);
  }

  .show-invoices-filter-radio > .radio__label:before {
    background-color: color(brand-blue-3)  !important;
    border-color: color(green-2);
  }

  .radio__element:checked + .radio__label:before {
    background-color: color(brand-blue-3)  !important;
    border-color: color(green-2);
  }
</style>
